import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { MaterialReactTable } from "material-react-table";
import toast from "react-hot-toast";
import apiClient from "../../../services/apiClient";
import PageLoader from "../../Loading/PageLoader";

const LLMConfigsList = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  // Modal + form state
  const [openModal, setOpenModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    model: "",
    provider: "",
    temperature: "",
    max_tokens: "",
    system_prompt: "",
  });

  // Provider selection state
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState("");

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    total_pages: 0,
  });

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    // Reset pagination when provider changes
    setPagination({ pageIndex: 0, pageSize: 10 });
  }, [selectedProvider]);

  useEffect(() => {
    fetchConfigs();
  }, [selectedProvider, pagination.pageIndex, pagination.pageSize]);

  // Fetch providers from voice-configs API
  const fetchProviders = async () => {
    try {
      const response = await apiClient.get("/llm-configs");
      if (response?.data && Array.isArray(response.data)) {
        // Extract unique providers
        const uniqueProviders = [...new Set(response.data.map(item => item.provider))];
        setProviders(uniqueProviders);
      }
    } catch (err) {
      toast.error("Failed to fetch providers");
      console.error(err);
    }
  };

  // Fetch all configs
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      let response;

      if (selectedProvider) {
        // If provider is selected, use provider-specific endpoint
        response = await apiClient.get("/llm-configs/provider", {
          page: pagination.pageIndex + 1,
          page_size: pagination.pageSize,
          provider: selectedProvider,
        });
      } else {
        // If no provider selected, use general endpoint
        response = await apiClient.get("/llm-configs", {
          page: pagination.pageIndex + 1,
          page_size: pagination.pageSize,
        });
      }

 
      setConfigs(response.data || []);
      setPaginationInfo({
        total_count: response.pagination?.total_count || 0,
        total_pages: response.pagination?.total_pages || 0,
      });
    } catch (err) {
      toast.error("Failed to fetch LLM configs");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleCreate = async () => {
    if (!formData.name) return toast.error("Name is required");
    setLoading(true);
    try {
      await apiClient.post("/llm-configs", {
        ...formData,
        temperature: parseFloat(formData.temperature),
        max_tokens: parseInt(formData.max_tokens),
      });
      toast.success("Config created successfully");
      handleCloseModal();
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to create config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      model: config.model,
      provider: config.provider,
      temperature: config.temperature,
      max_tokens: config.max_tokens,
      system_prompt: config.system_prompt,
    });
    setOpenModal(true);
  };

  const handleUpdate = async () => {
    if (!editingConfig) return;
    setLoading(true);
    try {
      await apiClient.put(`/llm-configs/${editingConfig._id}`, {
        ...formData,
        temperature: parseFloat(formData.temperature),
        max_tokens: parseInt(formData.max_tokens),
      });
      toast.success("Config updated successfully");
      handleCloseModal();
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to update config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this config?")) return;
    setLoading(true);
    try {
      await apiClient.delete(`/llm-configs/${id}`);
      toast.success("Config deleted successfully");
      fetchConfigs();
    } catch (err) {
      toast.error("Failed to delete config");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingConfig(null);
    setFormData({
      name: "",
      model: "",
      provider: "",
      temperature: "",
      max_tokens: "",
      system_prompt: "",
    });
  };

  const columns = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "model", header: "Model" },
    { accessorKey: "provider", header: "Provider" },
    { accessorKey: "temperature", header: "Temp" },
    { accessorKey: "max_tokens", header: "Max Tokens" },
    {
      accessorKey: "system_prompt",
      header: "System Prompt",
      size: 300,
    },
    {
      accessorKey: "actions",
      header: "Actions",
      size: 150,
      Cell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton size="small" onClick={() => handleEdit(row.original)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(row.original._id)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading && configs.length === 0) return <PageLoader />;

  return (

    <>
    
      {/* Header */}
      <div className="pageTitle1 d-flex justify-content-between align-items-center px-5 py-4">
        <h3>LLM Configs</h3>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          New Config
        </Button>
      </div>


    <Box sx={{ p: 3 }}>
      {/* <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          LLM Configs
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          New Config
        </Button>
      </Box> */}

      {/* Provider Selection */}
      <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Provider</InputLabel>
          <Select
            value={selectedProvider}
            label="Filter by Provider"
            onChange={(e) => setSelectedProvider(e.target.value)}
          >
            <MenuItem value="">All Providers</MenuItem>
            {providers.map((provider) => (
              <MenuItem key={provider} value={provider}>
                {provider}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          placeholder="Search configs..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          size="small"
        />
      </Box>

      {/* Table */}
      <MaterialReactTable
        columns={columns}
        data={configs.filter((c) =>
          c.name?.toLowerCase().includes(searchText.toLowerCase())
        )}
        manualPagination
        rowCount={paginationInfo.total_count}
        onPaginationChange={setPagination}
        state={{
          isLoading: loading,
          pagination: pagination,
        }}
        enableRowActions={false}
        enableColumnActions={false}
        enableSorting
        enablePagination
        muiTablePaginationProps={{
          showFirstButton: true,
          showLastButton: true,
        }}
      />

      {/* Popup Form */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingConfig ? "Edit Config" : "Create New Config"}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mt: 1, display: "grid", gap: 2 }}>
            {[
              "name",
              "model",
              "provider",
              "temperature",
              "max_tokens",
              "system_prompt",
            ].map((field) => (
              <TextField
                key={field}
                label={field.replace("_", " ").toUpperCase()}
                name={field}
                value={formData[field]}
                onChange={handleChange}
                fullWidth
                multiline={field === "system_prompt"}
                minRows={field === "system_prompt" ? 2 : 1}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={editingConfig ? handleUpdate : handleCreate}
          >
            {editingConfig ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    
    </>

  );
};

export default LLMConfigsList;
